/*
 * Vajihi Labs - Email Signature Builder
 * Validation layer: escaping, color/URL validation, image file validation.
 * Nothing in here talks to the DOM or Office.js — pure functions only.
 */

export function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR_RE.test(value.trim());
}

/**
 * Only http/https survive. Blocks javascript:, data:, vbscript:, and anything
 * else that could execute when a recipient clicks a "social" link — per the
 * spec's "reject javascript: and other executable URL schemes."
 */
export function sanitizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (url.username || url.password) return ""; // no embedded credentials
    return url.toString();
  } catch {
    return "";
  }
}

const CID_RE = /^cid:[a-z0-9._@-]+$/i;
const BLOB_RE = /^blob:/i;

/** Like sanitizeUrl, but also accepts a cid: reference for a future
 *  inline-attachment delivery path (not implemented yet — hosted URLs are
 *  the only delivery method this build actually supports end to end), and a
 *  blob: URL for an in-browser local logo preview. blob: URLs are same-origin
 *  object references (not executable, unlike javascript:/vbscript:) and only
 *  ever originate from this app's own local-file-upload preview path — they
 *  are never persisted to storage and never included in a share code. */
export function sanitizeLogoUrl(value: string): string {
  const trimmed = value.trim();
  if (CID_RE.test(trimmed)) return trimmed;
  if (BLOB_RE.test(trimmed)) return trimmed;
  return sanitizeUrl(trimmed);
}

export function isValidHttpUrl(value: string): boolean {
  return sanitizeUrl(value) !== "";
}

export const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MiB, per spec
export const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg"];

export interface ImageValidationResult {
  ok: boolean;
  reason?: string;
  objectUrl?: string;
  width?: number;
  height?: number;
}

/**
 * Validates a File beyond just its extension: checks declared MIME type,
 * size, and actually attempts to decode it as an image before accepting it.
 * A file that merely has a .png extension but isn't real image data will
 * fail the decode step and be rejected.
 */
export function validateImageFile(file: File): Promise<ImageValidationResult> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return Promise.resolve({ ok: false, reason: "Only PNG or JPG images are supported." });
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return Promise.resolve({ ok: false, reason: "Image is larger than 2 MB — please use a smaller file." });
  }

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ ok: true, objectUrl, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ ok: false, reason: "That file couldn't be read as an image — it may be corrupted." });
    };
    img.src = objectUrl;
  });
}

/** Detects any unresolved {{Token}} placeholder left in rendered text. */
export function findUnresolvedTokens(html: string): string[] {
  const matches = html.match(/\{\{\s*[^}]+?\s*\}\}/g);
  if (!matches) return [];
  return Array.from(new Set(matches));
}

export function isTokenValue(value: string): boolean {
  return /^\{\{\s*[^}]+?\s*\}\}$/.test(value.trim());
}
